import type {
  PaginatedResponse,
  EventSummaryResponse,
  MyEventSummaryResponse,
  EventResponse,
  EventPricingBreakdownResponse,
  EventPricingParams,
} from '@@/types/api';
import { useApi } from './use-api';

export function useEventsApi() {
  const { get, post, put, del, publicGet } = useApi('/api/v1/events');

  return {
    browse: (params?: Record<string, unknown>) =>
      publicGet<PaginatedResponse<EventSummaryResponse>>('/browse', { params }),
    search: (params: Record<string, unknown>) =>
      publicGet<PaginatedResponse<EventSummaryResponse>>('/search', { params }),
    getMyEvents: (params?: Record<string, unknown>) =>
      get<PaginatedResponse<MyEventSummaryResponse>>('/mine', { params }),
    getEvent: (id: string) => publicGet<EventResponse>(`/${id}`),
    getEventByShareToken: (token: string) =>
      publicGet<EventResponse>(`/share/${token}`),
    getEventAuthenticated: (id: string) => get<EventResponse>(`/${id}`),
    createEvent: (body: Record<string, unknown>) =>
      post<EventResponse>('', { body }),
    updateEvent: (id: string, body: Record<string, unknown>) =>
      put<EventResponse>(`/${id}`, { body }),
    transitionEvent: (id: string, action: string, paymentMethodId?: string) =>
      post<EventResponse>(`/${id}/transition`, {
        body: {
          action,
          ...(paymentMethodId && { payment_method_id: paymentMethodId }),
        },
      }),
    deleteEvent: (id: string) => del<undefined>(`/${id}`),
    getEventPricing: async (
      eventId: string,
      params: EventPricingParams,
    ): Promise<EventPricingBreakdownResponse> => {
      const response = await get<{ data: EventPricingBreakdownResponse }>(
        `/${eventId}/pricing`,
        {
          params: {
            registration_type_id: params.registration_type_id,
            party_size: params.party_size,
            mode: params.mode,
            ...(params.is_supplemental !== undefined && {
              is_supplemental: params.is_supplemental ? 1 : 0,
            }),
          },
        },
      );
      return response.data;
    },
    archiveEvent: (id: string) => post<EventResponse>(`/${id}/archive`),
    unarchiveEvent: (id: string) => post<EventResponse>(`/${id}/unarchive`),
  };
}
